import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, ToastComponent, ToastService } from '@/shared/components';
import { AuthService } from '@/services/auth.service';

@Component({
  selector: 'app-verify-code',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, ButtonComponent, ToastComponent],
  templateUrl: './verify-code.component.html',
})
export class VerifyCodeComponent implements OnInit {
  email = '';
  password = '';
  returnUrl = '/portal';
  mode: 'passwordReset' | 'login2FA' = 'passwordReset';
  code = '';
  digits: string[] = ['', '', '', '', '', ''];
  isLoading = false;
  submitted = false;
  isResending = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    const navigation = this.router.getCurrentNavigation();
    const historyState = history.state;
    const state = navigation?.extras?.state || historyState || {};

    this.email = state['email'] || '';
    this.password = state['password'] || '';
    this.returnUrl = state['returnUrl'] || '/portal';
    this.mode = state['mode'] || 'passwordReset';

    if (!this.email) {
      this.router.navigate([this.mode === 'login2FA' ? '/login' : '/recuperar-senha']);
    }
  }

  // Permite ao Angular rastrear os inputs pelo índice para evitar bugs visuais
  trackByIndex(index: number, obj: any): any {
    return index;
  }

  // Utilitário para focar num input específico
  private focusInput(index: number) {
    const input = document.getElementById(`code-input-${index}`) as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }

  // Seleciona todo o texto ao focar (melhor UX)
  onFocus(event: any) {
    event.target.select();
  }

  // Gerencia a digitação de números
  onDigitInput(event: any, index: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove não-números
    value = value.replace(/\D/g, '');

    // Se usuário colou ou digitou mais de 1 caractere, pega só o último
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Atualiza modelo
    this.digits[index] = value;
    this.code = this.digits.join('');

    // Se digitou um número, pula para o próximo automaticamente
    if (value && index < 5) {
      this.focusInput(index + 1);
    }
  }

  // Gerencia teclas especiais (Backspace, Setas)
  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    // BACKSPACE
    if (event.key === 'Backspace') {
      // Se o campo atual está vazio e não é o primeiro, volta para o anterior
      if (!input.value && index > 0) {
        event.preventDefault(); // Evita apagar 2 vezes se segurar
        this.digits[index - 1] = ''; // Limpa o anterior
        this.code = this.digits.join('');
        this.focusInput(index - 1); // Foca no anterior
      }
      // Se o campo tem valor, ele apaga naturalmente pelo evento input, não precisamos fazer nada aqui
      return;
    }

    // SETA ESQUERDA
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
    }

    // SETA DIREITA
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusInput(index + 1);
    }
  }

  onDigitPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    // Extrai apenas os primeiros 6 números
    const numbers = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    if (numbers.length > 0) {
      // Preenche o array
      numbers.forEach((num, i) => {
        this.digits[i] = num;
      });
      this.code = this.digits.join('');

      // Foca no próximo campo vazio ou no último
      const nextFocus = Math.min(numbers.length, 5);
      this.focusInput(nextFocus);

      // Se preencheu tudo, pode até submeter automático se quiser (opcional)
      // if (this.code.length === 6) this.onSubmit();
    }
  }

  onSubmit() {
    this.submitted = true;

    if (!this.code || this.code.length < 6) {
      return;
    }

    this.isLoading = true;

    if (this.mode === 'login2FA') {
      this.authService.login({
        email: this.email,
        password: this.password,
        code2FA: this.code
      }).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response && response.token) {
            this.toastService.show('success', 'Login realizado com sucesso!');
            const user = this.authService.getCurrentUser();
            if (user) {
              const redirectPath = this.returnUrl !== '/portal'
                ? this.returnUrl
                : this.authService.getRedirectPath(user.role);
              this.router.navigate([redirectPath]);
            }
          } else {
            this.toastService.show('error', 'Erro ao fazer login');
          }
        },
        error: (error) => {
          console.error('Error logging in with 2FA:', error);
          this.isLoading = false;
          const errorMessage = error.error?.message || 'Código 2FA inválido ou expirado.';
          this.toastService.show('error', errorMessage);
          this.code = '';
          this.digits = ['', '', '', '', '', ''];
          this.submitted = false;
          // Foca no primeiro input ao errar
          setTimeout(() => this.focusInput(0), 100);
        },
      });
    } else {
      this.authService.verifyResetCode(this.email, this.code).subscribe({
        next: () => {
          this.toastService.show('success', 'Código verificado com sucesso!');
          this.isLoading = false;
          this.router.navigate(['/recuperar-senha/nova-senha'], {
            state: { email: this.email, code: this.code },
          });
        },
        error: (error) => {
          console.error('Error verifying code:', error);
          this.isLoading = false;
          const errorMessage = error.error?.message || 'Código inválido ou expirado.';
          this.toastService.show('error', errorMessage);
        },
      });
    }
  }

  resendCode() {
    this.isResending = true;
    this.code = '';
    this.digits = ['', '', '', '', '', ''];
    setTimeout(() => this.focusInput(0), 100);

    if (this.mode === 'login2FA') {
      this.authService.login({
        email: this.email,
        password: this.password
      }).subscribe({
        next: (response) => {
          this.isResending = false;
          if (response.requires2FA) {
            this.toastService.show('success', 'Novo código enviado!');
          }
        },
        error: (error) => {
          this.isResending = false;
          const errorMessage = error.error?.message || 'Erro ao reenviar código.';
          this.toastService.show('error', errorMessage);
        },
      });
    } else {
      this.authService.requestPasswordReset(this.email).subscribe({
        next: () => {
          this.toastService.show('success', 'Novo código enviado!');
          this.isResending = false;
        },
        error: (error) => {
          console.error('Error resending code:', error);
          this.isResending = false;
          const errorMessage = error.error?.message || 'Erro ao reenviar código.';
          this.toastService.show('error', errorMessage);
        },
      });
    }
  }

  maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) return `${localPart[0]}***@${domain}`;
    const maskedLocal = localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1];
    return `${maskedLocal}@${domain}`;
  }
}